import {
  ADMIN_LABEL_VARIANT_NAMES,
  INNER_LABEL_VARIANT_NAMES,
  type HotelCardProps,
  type LabelVariantValue,
  type MapHotelCardProps,
} from 'src/components/HotelCard/types';
import { i18n } from '@lt/localization';
import type { Coordinates } from 'src/components/YandexMap/types';
import { objToQueryString } from 'src/utils/uriUtils';
import type { SearchType } from 'src/types/search';

import type { ILabel } from '@lt/api/dist/methods/packages/types';

import { addDays, isValid, parseISO } from 'date-fns';
import { formatDate } from '@lt/components/utils/date';

import type { HotelRendererProps } from './types';

// Функция, которая добавляет в query инфу по фильтрам, реквест айди и серч тайпу
export const buildHotelURL = (
  date: string,
  price: number,
  nights: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: any,
  requestId: string,
  link: string,
  searchType: SearchType,
) => {
  const {
    operators,
    instantConfirm,
    ltExtras,
    freeCancel,
    meals,
    paymentBenefits,
  } = filters || {};

  const query = objToQueryString({
    request_id: requestId,
    search_type: searchType,
    offer_price: price,
    offer_nights: nights,
    offer_date: date,
    offer_operators: operators,
    offer_instant_confirm: instantConfirm,
    offer_lt_extras: ltExtras,
    offer_free_cancel: freeCancel,
    offer_meals: meals,
    offer_labels: [paymentBenefits].join(),
  });

  return `${link}${query ? `?${query}` : ''}`;
};

export const getHotelLocation = (city: string, regionName: string) => {
  if (!city && !regionName) return '';

  if (city === regionName || !regionName) return city;

  if (!city) return regionName;

  return `${city}, ${regionName}`;
};

/**
 * Часть лейблов приходит из админки, а часть формируется на фронте,
 * эта функция разделяет лейблы по месту на карточке, где они должны отображаться
 * и приводит данные по лейблам к единому формату
 */
export const getHotelCardLabels = (
  labels: ILabel[],
  instantConfirm: boolean,
  isFreeCancellationPolicy: boolean,
  isHotelOnly: boolean,
): {
  innerLabels: LabelVariantValue[];
  headerLabel?: ILabel;
} => {
  const headerLabel = labels.find((label) => label.context?.search === 1);
  const innerLabels: LabelVariantValue[] = labels
    .filter((label) => label.context?.search === 2)
    .map((el) => ({
      id: String(el.id),
      variant: ADMIN_LABEL_VARIANT_NAMES[el.style],
      tooltipText: el.description,
      text: el.title,
    }));

  // Для отелей не показываем плашку "мгновенного подтверждения"
  if (instantConfirm && !isHotelOnly) {
    innerLabels.unshift({
      tooltipText: i18n.t('hotelcard.tag.instant.tooltip'),
      text: i18n.t('hotelcard.tag.instant.text'),
      id: INNER_LABEL_VARIANT_NAMES.INSTANT_CONFIRM,
      variant: INNER_LABEL_VARIANT_NAMES.INSTANT_CONFIRM,
    });
  }

  // Для отелей показываем плашку "долгого подтверждения"
  if (!instantConfirm && isHotelOnly) {
    innerLabels.unshift({
      tooltipText: i18n.t('hotelcard.tag.notToConfirmAtOnce.tooltip'),
      text: i18n.t('hotelcard.tag.notToConfirmAtOnce.text'),
      id: INNER_LABEL_VARIANT_NAMES.NOT_TO_CONFIRM_AT_ONCE,
      variant: INNER_LABEL_VARIANT_NAMES.NOT_TO_CONFIRM_AT_ONCE,
    });
  }

  if (isFreeCancellationPolicy) {
    innerLabels.unshift({
      text: i18n.t('hotelcard.tag.free_cancellation'),
      id: INNER_LABEL_VARIANT_NAMES.FREE_CANCELLATION,
      variant: INNER_LABEL_VARIANT_NAMES.FREE_CANCELLATION,
    });
  }

  return {
    headerLabel,
    innerLabels: innerLabels.slice(0, 6),
  };
};

export const getDatesInfo = (startDate: string, nightsCount: number) => {
  const dateAsDate = parseISO(startDate);
  const formattedStartDate = isValid(dateAsDate)
    ? formatDate(dateAsDate, 'd MMM')
    : startDate;
  const endDate = addDays(new Date(startDate), nightsCount);
  const formattedEndDate = isValid(endDate)
    ? formatDate(endDate, 'd MMM')
    : endDate;

  return `${formattedStartDate} - ${formattedEndDate}, ${nightsCount}`;
};

export function getHotelCardProps(
  hotelRendererProps: HotelRendererProps,
): HotelCardProps {
  const {
    images,
    hotelPageVisited,
    bonusCount,
    currencyCode,
    stars,
    name,
    city,
    regionName,
    rating,
    features,
    instantConfirm,
    isFlexDates,
    price,
    labels = [],
    isFreeCancellationPolicy,
    isHotelOnly,
    availability,
    startDate,
    nightsCount,
    link,
    className,
    penalizedFilters = [],
    offerFilters,
    requestId,
    long,
    lat,
    id,
    setActiveHotel,
    adjacentTours,
    openInCurrentTab,
    isMobile,

    cashbackIdentifier,
    customCashbackIconURL,
    customCashbackText,
    customCashbackTooltipText,
    isCashbackEnable,
  } = hotelRendererProps;

  const onLongHover = () => {
    if (penalizedFilters.length || !long || !lat) {
      return;
    }

    const activeHotelData = {
      id,
      coordinates: [long, lat] as Coordinates,
      visited: hotelPageVisited,
    };

    setActiveHotel(activeHotelData);
  };

  const { innerLabels, headerLabel } = getHotelCardLabels(
    labels,
    instantConfirm,
    isFreeCancellationPolicy,
    isHotelOnly,
  );

  const adjacentToursLimit = isMobile ? 2 : 3;

  return {
    id,
    hotelPageHref: buildHotelURL(
      startDate,
      price,
      nightsCount,
      offerFilters,
      requestId || '',
      link,
      isHotelOnly ? 'hotel' : 'package',
    ),
    images,
    hotelPageVisited,
    bonusCount: isCashbackEnable ? bonusCount : 0,
    currencyCode,
    stars,
    hotelName: name,
    hotelLocation: getHotelLocation(city, regionName),
    rating,
    features,
    instantConfirm,
    isFlexDates,
    price,
    innerLabels,
    headerLabel,
    isHotelOnly,
    availability,
    openInCurrentTab,
    datesInfo: getDatesInfo(startDate, nightsCount),
    className,
    penalizedFilters,
    onLongHover,
    adjacentTours: adjacentTours?.slice(0, adjacentToursLimit).map((tour) => ({
      ...tour,
      datesInfo: getDatesInfo(tour.startDate, tour.nightsCount),
      href: buildHotelURL(
        tour.startDate,
        tour.price,
        tour.nightsCount,
        offerFilters,
        requestId || '',
        link,
        isHotelOnly ? 'hotel' : 'package',
      ),
    })),

    cashbackIdentifier,
    customCashbackIconURL,
    customCashbackText,
    customCashbackTooltipText,
  };
}

export function getMapHotelCardProps(
  hotelRendererProps: HotelRendererProps,
): MapHotelCardProps {
  const {
    id,
    images,
    hotelPageVisited,
    bonusCount,
    currencyCode,
    stars,
    name,
    city,
    regionName,
    rating,
    features,
    instantConfirm,

    price,
    labels = [],
    isFreeCancellationPolicy,
    isHotelOnly,
    availability,

    link,
    className,
    resetPopupData = () => null,
    startDate,
    nightsCount,
    offerFilters,
    requestId,
    openInCurrentTab,

    onCloseMapPopup,

    cashbackIdentifier,
    customCashbackIconURL,
    customCashbackText,
    customCashbackTooltipText,
    isCashbackEnable,
  } = hotelRendererProps;

  const { innerLabels, headerLabel } = getHotelCardLabels(
    labels,
    instantConfirm,
    isFreeCancellationPolicy,
    isHotelOnly,
  );

  return {
    id,
    onCloseIconClick: () => {
      resetPopupData();
      onCloseMapPopup?.();
    },
    hotelPageHref: buildHotelURL(
      startDate,
      price,
      nightsCount,
      offerFilters,
      requestId || '',
      link,
      isHotelOnly ? 'hotel' : 'package',
    ),
    images,
    hotelPageVisited,
    bonusCount: isCashbackEnable ? bonusCount : 0,
    currencyCode,
    stars,
    hotelName: name,
    hotelLocation: getHotelLocation(city, regionName),
    rating,
    features,
    instantConfirm,
    price,
    innerLabels,
    headerLabel,
    isFreeCancellationPolicy,
    isHotelOnly,
    availability,
    openInCurrentTab,
    className,

    cashbackIdentifier,
    customCashbackIconURL,
    customCashbackText,
    customCashbackTooltipText,
  };
}
